import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/api";
import axiosPrivate from "../../services/axios/axiosPrivate";

export const createFilesUpload = createAsyncThunk(
    'files/createFilesUpload',
    async (formData, { rejectWithValue }) => {
        try {
            const url = api.filesUpload.create
            const response = await axiosPrivate.post(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                }
            });
            return response.data;
        }
        catch (err) {
            return rejectWithValue(err?.response?.data?.message || "Unable to upload file");
        }
    }
)

export const getSingleUploadedFile = createAsyncThunk(
    'files/getSingleUploadedFile',
    async ({ type, id }, { rejectWithValue }) => {
        try {
            const url = `${api.filesUpload.getSingleFile}?type=${type}&id=${id}`;
            const response = await axiosPrivate.get(url,
                {
                    responseType: "blob",
                }
            );
            const contentType = response.headers["content-type"];
            // Determine filename from header
            const contentDisposition = response.headers["content-disposition"] || "";
            const match = contentDisposition.match(/filename="?(.+)"?/);
            const fileName = match ? match[1] : `${type}`;
            // Create blob directly from response
            const blob = new Blob([response.data], { type: contentType });
            const fileUrl = URL.createObjectURL(blob);

            return {
                fileUrl,
                contentType,
                fileName,
            };
        }
        catch (err) {
            return rejectWithValue(err?.response?.data?.message || "Unable to fetch single file");
        }
    }
)

export const getAllUploadedFile = createAsyncThunk(
    'files/getAllUploadedFile',
    async (id, { rejectWithValue }) => {
        try {
            const url = `${api.filesUpload.getAllFiles}?id=${id}`;
            const response = await axiosPrivate.get(url);
            return response.data.data;
        }
        catch (err) {
            return rejectWithValue(err?.response?.data?.message || "Unable to fetch all files");
        }
    }
)

const filesUploadSlice = createSlice({
    name: 'filesUpload',
    initialState: {
        uploadedFile: null,
        allFiles: [],
        uploadStatus: {},
        fetchStatus: 'idle',
        fetchAllStatus: 'idle',
        loading: false,
        error: null
    },
    reducers: {
        resetUploadStatus: (state, action) => {
            const type = action.payload; 
            if (type) {
                state.uploadStatus[type] = 'idle';
            } else {
                state.uploadStatus = {};
            }
            state.error = null;
            state.uploadedFile = null;
        }
    },
    extraReducers: (builders) => {
        builders
            .addCase(createFilesUpload.pending,
                (state,action) => {
                    // action.meta.arg contains the formData sent to the thunk
                    const type = action.meta.arg.get('type');
                    state.uploadStatus[type] = 'loading';
                    state.loading = true;
                })
            .addCase(createFilesUpload.fulfilled,
                (state, action) => {
                    const type = action.meta.arg.get('type');
                    state.uploadStatus[type] = 'succeeded';
                    state.uploadedFile = action.payload;
                    state.loading = false;
                })
            .addCase(createFilesUpload.rejected,
                (state, action) => {
                    const type = action.meta.arg.get('type');
                    state.uploadStatus[type] = 'failed';
                    state.error = action.payload;
                    state.loading = false;
                })
            .addCase(getSingleUploadedFile.pending,
                (state) => {
                    state.fetchStatus = 'loading';
                    state.loading = true;
                })
            .addCase(getSingleUploadedFile.fulfilled,
                (state, action) => {
                    state.fetchStatus = 'succeeded';
                    state.uploadedFile = action.payload;
                    state.loading = false;
                })
            .addCase(getSingleUploadedFile.rejected,
                (state, action) => {
                    state.fetchStatus = 'failed';
                    state.error = action.payload;
                    state.loading = false;
                })
            .addCase(getAllUploadedFile.pending,
                (state) => {
                    state.fetchAllStatus = 'loading';
                    state.loading = true;
                })
            .addCase(getAllUploadedFile.fulfilled,
                (state, action) => {
                    state.fetchAllStatus = 'succeeded';
                    state.allFiles = action.payload;
                    state.loading = false;
                })
            .addCase(getAllUploadedFile.rejected,
                (state, action) => {
                    state.fetchAllStatus = 'failed';
                    state.error = action.payload;
                    state.loading = false;
                })
    }
})

export const { resetUploadStatus } = filesUploadSlice.actions;
export default filesUploadSlice.reducer;